import React,{useEffect,useState} from 'react';
import {FiClock,FiTarget,FiX} from 'react-icons/fi';
import {storyRuntime,validTarget,targetProgress,clockTime,runtimeGauge,runtimeGuidance} from '../lib/storyRuntime';

const choices=[60,120,180,300,600];
export default function StoryRuntimeBar({blocks=[],target,onSetTarget,readOnly=false,compact=false,gauge=false,caption='Selected interview footage only'}){
 const {seconds,words}=storyRuntime(blocks);
 const normalized=validTarget(target);
 const info=targetProgress(seconds,normalized);
 const gaugeInfo=runtimeGauge(seconds,normalized);
 const guidance=runtimeGuidance(seconds,normalized);
 const [editing,setEditing]=useState(false),[draft,setDraft]=useState(normalized||180);
 useEffect(()=>{if(!editing)setDraft(normalized||180);},[normalized,editing]);
 const apply=seconds=>{
  const valid=validTarget(seconds);
  if(valid===null)return;
  onSetTarget?.(valid);setDraft(valid);setEditing(false);
 };
 const main=<div className="runtime-progress-display">
   <div className="runtime-progress-details">
    <span><FiClock aria-hidden="true"/> <strong>{clockTime(seconds)}</strong>{normalized!==null&&<span className="runtime-target-detail"> / {clockTime(normalized)} target</span>}</span>
    <span className="runtime-progress-words">{words.toLocaleString()} words</span>
   </div>
   {info&&<div className="runtime-visual-track" role="progressbar" aria-label="Selected footage relative to target runtime" aria-valuenow={Math.round(seconds)} aria-valuemin={0} aria-valuemax={Math.max(Math.ceil(seconds),normalized)} aria-valuetext={clockTime(seconds)+" of "+clockTime(normalized)+" target"}>
    <span className="runtime-visual-fill" style={{width:info.over?info.targetPercent+'%':info.fillPercent+'%'}}/>
    {info.over&&<span className="runtime-visual-over" style={{width:(100-info.targetPercent)+'%'}}/>}
    <span className="runtime-visual-target" title={'Target '+clockTime(normalized)}/>
   </div>}
   <div className="runtime-progress-bottom">
    <small>{normalized===null?'Set an optional project target to track the assembly length.':info.over?(clockTime(info.secondsOver)+' over target · a guide, not a limit'):'Approximate interview runtime · target is a guide'}</small>
    {!readOnly&&<button type="button" aria-expanded={editing} onClick={()=>setEditing(v=>!v)}>{normalized===null?'Set target':'Adjust target'}</button>}
   </div>
  </div>;
 return <section className={`story-runtime-bar${compact?' is-header-runtime':''}${gauge?' runtime-gauge-footer':''}`} aria-label="Assembly runtime indicator">
  {gauge ? <>
   <div className="runtime-gauge-heading">
    <strong className="runtime-gauge-title"><FiTarget aria-hidden="true"/> Target duration</strong>
    <div className="runtime-gauge-values">
     <span className="runtime-gauge-current-value">Current <strong>{clockTime(seconds)}</strong></span>
     <span className="runtime-gauge-goal-value">Target <strong>{normalized===null?'Not set':clockTime(normalized)}</strong></span>
    </div>
    {!readOnly&&<button type="button" className="runtime-gauge-adjust"
      aria-expanded={editing} onClick={()=>setEditing(value=>!value)}>
      {normalized===null?'Set target':'Adjust'}
    </button>}
   </div>
   <div className="runtime-gauge-range" role="meter"
     aria-label="Current Assembly duration on the target duration scale"
     aria-valuemin={0} aria-valuemax={Math.ceil(gaugeInfo.maxSeconds)}
     aria-valuenow={Math.round(seconds)}
     aria-valuetext={clockTime(seconds)+(normalized===null?' current, no target set':' current, '+clockTime(normalized)+' target')}>
    <div className={normalized===null?'runtime-gauge-track is-unset':'runtime-gauge-track'}
      style={normalized===null?undefined:{background:gaugeInfo.gradient,'--gauge-target-percent':gaugeInfo.targetPercent+'%'}}/>
    <span className="runtime-gauge-now-marker" style={{left:gaugeInfo.currentPercent+'%'}}
      title={'Current Assembly '+clockTime(seconds)} aria-hidden="true">
      <span className="runtime-gauge-playhead-cap"/>
      <span className="runtime-gauge-playhead-line"/>
    </span>
   </div>
   <div className="runtime-gauge-scale">
    <span>0:00</span>
    <span className={'runtime-gauge-guidance is-'+guidance.state} role="status">{guidance.message}</span>
    <span>{clockTime(gaugeInfo.maxSeconds)}</span>
   </div>
  </> : compact ? <>
   <div className="runtime-header-row">
    <span className="runtime-header-label"><FiClock aria-hidden="true"/> <strong>{clockTime(seconds)}</strong>
      <span className="runtime-header-target">{normalized!==null?` / ${clockTime(normalized)} target`:' · No target'}</span>
    </span>
    {!readOnly&&<button type="button" className="runtime-header-adjust" aria-expanded={editing}
      onClick={()=>setEditing(v=>!v)}>{normalized===null?'Set target':'Adjust target'}</button>}
   </div>
   {info&&<div className="runtime-visual-track" role="progressbar" aria-label="Assembly runtime relative to target"
      aria-valuenow={Math.round(seconds)} aria-valuemin={0} aria-valuemax={Math.max(Math.ceil(seconds),normalized)}
      aria-valuetext={clockTime(seconds)+' of '+clockTime(normalized)+' target'}>
     <span className="runtime-visual-fill" style={{width:(info.over?info.targetPercent:info.fillPercent)+'%'}}/>
     {info.over&&<span className="runtime-visual-over" style={{width:(100-info.targetPercent)+'%'}}/>}
     <span className="runtime-visual-target" title={'Target '+clockTime(normalized)}/>
   </div>}
  </> : <>
   <div className="runtime-topline"><span><FiTarget aria-hidden="true"/> ASSEMBLY RUNTIME</span><small>{caption}</small></div>
   {main}
  </>}
  {!readOnly&&editing&&<div className="runtime-target-popover">
    <div className="runtime-target-popover-head"><strong>Project runtime target</strong><button onClick={()=>setEditing(false)} aria-label="Close target settings"><FiX/></button></div>
    <p>Use this as a planning guide for the whole assembly. Nothing gets cut automatically.</p>
    <div className="runtime-quick-choices" role="group" aria-label="Suggested target durations">{choices.map(n=><button key={n} className={draft===n?'is-active':''} onClick={()=>setDraft(n)}>{clockTime(n)}</button>)}</div>
    <label className="runtime-custom-label" htmlFor="runtime-target-range">Custom target <strong>{clockTime(draft)}</strong></label>
    <input id="runtime-target-range" type="range" min="30" max="900" step="15" value={Math.min(draft,900)} onChange={e=>setDraft(Number(e.target.value))}/>
    <label className="runtime-custom-label" htmlFor="runtime-target-minutes">Or enter minutes</label>
    <input id="runtime-target-minutes" className="runtime-target-minutes" type="number" min="0.5" max="60" step="0.25" value={draft/60}
      onChange={e=>{const seconds=Math.round(Number(e.target.value)*60);if(Number.isFinite(seconds)&&seconds>=30&&seconds<=3600)setDraft(seconds);}}/>
    <div className="runtime-target-popover-actions">
      {normalized!==null&&<button onClick={()=>{onSetTarget?.(null);setEditing(false);}}>Remove target</button>}
      <button className="runtime-target-save" onClick={()=>apply(draft)}>Save target</button>
    </div>
  </div>}
 </section>;
}
